import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import KpiForm from "../components/KpiForm";
import KpiList from "../components/KpiList";
import { FaPen, FaTimes, FaCheck } from "react-icons/fa";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  Typography,
  Paper,
  useTheme,
} from "@mui/material";

export default function AdminView({ user }) {
  const theme = useTheme();
  const [perusahaanList, setPerusahaanList] = useState([]);
  const [selectedPerusahaan, setSelectedPerusahaan] = useState("");
  const [newPerusahaan, setNewPerusahaan] = useState("");
  const [tahun, setTahun] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleKpiAdded = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleResetSelection = () => {
    setTahun("");
    setSelectedPerusahaan("");
    setShowConfirmModal(false);
  };

  useEffect(() => {
    const loadPerusahaan = async () => {
      const snapshot = await getDocs(collection(db, "kpi_collections"));
      setPerusahaanList(snapshot.docs.map((doc) => doc.id));
    };
    loadPerusahaan();
  }, []);

  const handleCreatePerusahaan = async () => {
    if (!newPerusahaan.trim()) return;
    if (perusahaanList.includes(newPerusahaan)) return alert("Sudah ada!");

    await setDoc(doc(db, "kpi_collections", newPerusahaan), {});
    await setDoc(doc(db, "users", newPerusahaan), {
      username: newPerusahaan,
      password: `${newPerusahaan}123`,
      role: "pimpinan",
      company: newPerusahaan,
    });
    setNewPerusahaan("");
    setSelectedPerusahaan(newPerusahaan);
    setPerusahaanList([...perusahaanList, newPerusahaan]);
  };

  // Styles that adapt to theme
  const styles = {
    dashboard: {
      padding: theme.spacing(3),
      backgroundColor: theme.palette.background.default,
      minHeight: "100vh",
    },
    panel: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(3),
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(3),
      boxShadow: theme.shadows[2],
    },
    infoCard: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(2),
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1300,
    },
    modalContent: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(4),
      borderRadius: theme.shape.borderRadius,
      maxWidth: 500,
      width: "100%",
    },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
  };

  return (
    <Box sx={styles.dashboard}>
      {!selectedPerusahaan && (
        <Paper sx={styles.panel}>
          <Typography variant="h5" gutterBottom>
            Kelola Perusahaan
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel>Pilih Perusahaan</InputLabel>
            <Select
              value={selectedPerusahaan}
              onChange={(e) => setSelectedPerusahaan(e.target.value)}
              label="Pilih Perusahaan"
            >
              <MenuItem value="">-- Pilih Perusahaan --</MenuItem>
              {perusahaanList.map((p, i) => (
                <MenuItem key={i} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Nama Perusahaan Baru"
            value={newPerusahaan}
            onChange={(e) => setNewPerusahaan(e.target.value)}
          />

          <Button
            variant="contained"
            onClick={handleCreatePerusahaan}
            sx={{ mt: 2 }}
          >
            + Tambah
          </Button>
        </Paper>
      )}

      {selectedPerusahaan && !tahun && (
        <Paper sx={styles.panel}>
          <Typography variant="h5" gutterBottom>
            Pilih tahun untuk {selectedPerusahaan}
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel>Pilih Tahun</InputLabel>
            <Select
              value={tahun}
              onChange={(e) => setTahun(e.target.value)}
              label="Pilih Tahun"
            >
              <MenuItem value="">-- Pilih tahun --</MenuItem>
              <MenuItem value="2025">2025</MenuItem>
              <MenuItem value="2026">2026</MenuItem>
              <MenuItem value="2027">2027</MenuItem>
              <MenuItem value="2028">2028</MenuItem>
              <MenuItem value="2029">2029</MenuItem>
              <MenuItem value="2030">2030</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      {selectedPerusahaan && tahun && (
        <Paper sx={styles.panel}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={styles.infoCard}
          >
            <Box>
              <Typography variant="h6">KPI: {selectedPerusahaan}</Typography>
              <Typography>
                Tahun: {tahun.replace("_", " ").toUpperCase()}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setShowConfirmModal(true)}
              startIcon={<FaPen />}
            >
              Ubah Pilihan
            </Button>
          </motion.div>

          <KpiForm
            perusahaan={selectedPerusahaan}
            tahun={tahun}
            role={user.role}
            onSuccess={handleKpiAdded}
          />

          <KpiList
            perusahaan={selectedPerusahaan}
            tahun={tahun}
            key={refreshKey}
          />
        </Paper>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <Box sx={styles.modalOverlay}>
          <motion.div
            style={styles.modalContent}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <Typography variant="h6" gutterBottom>
              Konfirmasi Perubahan
            </Typography>
            <Typography paragraph>
              Anda yakin ingin mengubah pilihan perusahaan/tahun?
            </Typography>

            <Box sx={styles.modalActions}>
              <Button
                variant="outlined"
                onClick={() => setShowConfirmModal(false)}
                startIcon={<FaTimes />}
              >
                Batal
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleResetSelection}
                startIcon={<FaCheck />}
              >
                Ya, Ubah
              </Button>
            </Box>
          </motion.div>
        </Box>
      )}
    </Box>
  );
}
